import React, { useState, useEffect, useRef } from 'react';
import agentService, { EventType, Role } from '../services/AgentService';

const AgentChat = ({ documentContext = {} }) => {
    const [messages, setMessages] = useState([]);
    const [inputMessage, setInputMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isConnected, setIsConnected] = useState(false);
    const [sessionId] = useState(`session_${Date.now()}`);
    const messagesEndRef = useRef(null);
    const currentRunRef = useRef(null);

    useEffect(() => {
        // Connect to agent service
        agentService.connect()
            .then(() => {
                setIsConnected(true);
                console.log('Agent service connected');
            })
            .catch(error => {
                console.error('Failed to connect to agent service:', error);
                setIsConnected(false);
            });

        return () => {
            if (currentRunRef.current) {
                currentRunRef.current.unsubscribe();
            }
            agentService.disconnect();
        };
    }, []);

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    const handleSendMessage = async () => {
        if (!inputMessage.trim() || isLoading || !isConnected) return;

        const userMessage = {
            id: `msg_${Date.now()}`,
            role: Role.USER,
            content: inputMessage.trim(),
            timestamp: new Date().toISOString()
        };

        // Add user message to chat
        setMessages(prev => [...prev, userMessage]);
        setInputMessage('');
        setIsLoading(true);

        // Prepare context with document information
        const context = {
            entities: documentContext.entities || {},
            entityCounts: documentContext.entityCounts || {},
            mitreData: documentContext.mitreData || {},
            documentId: documentContext.documentId
        };

        try {
            let assistantMessage = {
                id: `msg_${Date.now()}`,
                role: Role.ASSISTANT,
                content: '',
                timestamp: new Date().toISOString(),
                isStreaming: true
            };

            // Add placeholder assistant message
            setMessages(prev => [...prev, assistantMessage]);

            // Send message using AG-UI protocol
            const subscription = agentService.sendMessage(
                userMessage.content,
                sessionId,
                context
            ).subscribe({
                next: (event) => {
                    console.log('AG-UI Event:', event.type, event);
                    
                    switch (event.type) {
                        case EventType.RUN_STARTED:
                            console.log('Agent run started:', event.runId);
                            break;
                            
                        case EventType.TEXT_MESSAGE_START:
                            // Message started, reset content
                            assistantMessage.content = '';
                            break;
                            
                        case EventType.TEXT_MESSAGE_CONTENT:
                            // Stream content
                            assistantMessage.content += event.content;
                            setMessages(prev => 
                                prev.map(msg => 
                                    msg.id === assistantMessage.id 
                                        ? { ...msg, content: assistantMessage.content }
                                        : msg
                                )
                            );
                            break;
                            
                        case EventType.TEXT_MESSAGE_END:
                            // Message complete
                            assistantMessage.isStreaming = false;
                            setMessages(prev => 
                                prev.map(msg => 
                                    msg.id === assistantMessage.id 
                                        ? { ...msg, isStreaming: false }
                                        : msg
                                )
                            );
                            break;
                            
                        case EventType.TOOL_CALL_START:
                            // Show tool usage
                            const toolMessage = {
                                id: `tool_${Date.now()}`,
                                role: 'system',
                                content: `🔧 Using tool: ${event.toolName}`,
                                timestamp: new Date().toISOString(),
                                isToolCall: true
                            };
                            setMessages(prev => [...prev.slice(0, -1), toolMessage, assistantMessage]);
                            break;
                            
                        case EventType.TOOL_CALL_RESULT:
                            // Tool completed
                            if (event.error) {
                                const errorMessage = {
                                    id: `error_${Date.now()}`,
                                    role: 'system',
                                    content: `❌ Tool error: ${event.error}`,
                                    timestamp: new Date().toISOString(),
                                    isError: true
                                };
                                setMessages(prev => [...prev.slice(0, -1), errorMessage, assistantMessage]);
                            }
                            break;
                            
                        case EventType.RUN_FINISHED:
                            setIsLoading(false);
                            currentRunRef.current = null;
                            console.log('Agent run finished');
                            break;
                            
                        case EventType.RUN_ERROR:
                            setIsLoading(false);
                            currentRunRef.current = null;
                            
                            const errorMsg = {
                                id: `error_${Date.now()}`,
                                role: Role.ASSISTANT,
                                content: `❌ I encountered an error: ${event.error}`,
                                timestamp: new Date().toISOString(),
                                isError: true
                            };
                            
                            setMessages(prev => [...prev.slice(0, -1), errorMsg]);
                            break;
                    }
                },
                error: (error) => {
                    console.error('Agent error:', error);
                    setIsLoading(false);
                    currentRunRef.current = null;
                    
                    const errorMsg = {
                        id: `error_${Date.now()}`,
                        role: Role.ASSISTANT,
                        content: `❌ Connection error: ${error.message}`,
                        timestamp: new Date().toISOString(),
                        isError: true
                    };
                    
                    setMessages(prev => [...prev.slice(0, -1), errorMsg]);
                },
                complete: () => {
                    setIsLoading(false);
                    currentRunRef.current = null;
                }
            });

            currentRunRef.current = subscription;

        } catch (error) {
            console.error('Chat error:', error);
            setIsLoading(false);
            
            const errorMsg = {
                id: `error_${Date.now()}`,
                role: Role.ASSISTANT,
                content: `❌ Failed to send message: ${error.message}`,
                timestamp: new Date().toISOString(),
                isError: true
            };
            
            setMessages(prev => [...prev.slice(0, -1), errorMsg]);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    const clearChat = () => {
        setMessages([]);
        agentService.clearConversation(sessionId);
    };

    const exportChatTranscript = async () => {
        try {
            const response = await fetch('/api/export-chat-transcript', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ messages })
            });

            if (response.ok) {
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `chat-transcript-${Date.now()}.md`;
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
                document.body.removeChild(a);
            } else {
                console.error('Failed to export chat transcript');
            }
        } catch (error) {
            console.error('Error exporting chat transcript:', error);
        }
    };

    const abortCurrentRun = () => {
        if (currentRunRef.current) {
            currentRunRef.current.unsubscribe();
            currentRunRef.current = null;
            setIsLoading(false);
        }
    };

    const formatMessage = (message) => {
        if (message.isToolCall) {
            return (
                <div className="alert alert-info py-2 mb-2">
                    <small>{message.content}</small>
                </div>
            );
        }
        
        if (message.isError) {
            return (
                <div className="alert alert-danger py-2 mb-2">
                    <small>{message.content}</small>
                </div>
            );
        }
        
        return (
            <div className={`message ${message.role === Role.USER ? 'user-message' : 'assistant-message'}`}>
                <div className="message-content">
                    {message.content}
                    {message.isStreaming && <span className="streaming-cursor">|</span>}
                </div>
                <small className="text-muted">
                    {new Date(message.timestamp).toLocaleTimeString()}
                </small>
            </div>
        );
    };

    return (
        <div className="card h-100">
            <div className="card-header d-flex justify-content-between align-items-center">
                <h5 className="mb-0">
                    <i className="fas fa-robot me-2"></i>
                    Cyber Intelligence Agent
                </h5>
                <div>
                    <span className={`badge ${isConnected ? 'bg-success' : 'bg-danger'} me-2`}>
                        {isConnected ? 'Connected' : 'Disconnected'}
                    </span>
                    {isLoading && (
                        <button 
                            className="btn btn-sm btn-outline-warning me-2"
                            onClick={abortCurrentRun}
                            title="Abort current request"
                        >
                            <i className="fas fa-stop"></i>
                        </button>
                    )}
                    <button 
                        className="btn btn-sm btn-outline-primary me-2"
                        onClick={exportChatTranscript}
                        title="Export Chat Transcript"
                        disabled={messages.length === 0}
                    >
                        <i className="fas fa-download"></i>
                    </button>
                    <button 
                        className="btn btn-sm btn-outline-secondary"
                        onClick={clearChat}
                        title="Clear chat"
                    >
                        <i className="fas fa-trash"></i>
                    </button>
                </div>
            </div>
            
            <div className="card-body d-flex flex-column" style={{ height: '400px' }}>
                <div className="flex-grow-1 overflow-auto mb-3" style={{ maxHeight: '300px' }}>
                    {messages.length === 0 ? (
                        <div className="text-center text-muted py-4">
                            <i className="fas fa-comments fa-2x mb-2"></i>
                            <p>Start a conversation with the Cyber Intelligence Agent</p>
                            <small>Ask about entities, MITRE ATT&CK techniques, or threat intelligence</small>
                        </div>
                    ) : (
                        messages.map(message => (
                            <div key={message.id} className="mb-2">
                                {formatMessage(message)}
                            </div>
                        ))
                    )}
                    <div ref={messagesEndRef} />
                </div>
                
                <div className="input-group">
                    <textarea
                        className="form-control"
                        placeholder="Ask about cybersecurity entities, MITRE techniques, or threat intelligence..."
                        value={inputMessage}
                        onChange={(e) => setInputMessage(e.target.value)}
                        onKeyPress={handleKeyPress}
                        disabled={!isConnected || isLoading}
                        rows="2"
                        style={{ resize: 'none' }}
                    />
                    <button
                        className="btn btn-primary"
                        onClick={handleSendMessage}
                        disabled={!isConnected || isLoading || !inputMessage.trim()}
                    >
                        {isLoading ? (
                            <i className="fas fa-spinner fa-spin"></i>
                        ) : (
                            <i className="fas fa-paper-plane"></i>
                        )}
                    </button>
                </div>
            </div>
            
            <style jsx>{`
                .user-message {
                    text-align: right;
                    margin-bottom: 10px;
                }
                
                .assistant-message {
                    text-align: left;
                    margin-bottom: 10px;
                }
                
                .message-content {
                    display: inline-block;
                    max-width: 80%;
                    padding: 8px 12px;
                    border-radius: 12px;
                    word-wrap: break-word;
                    white-space: pre-wrap;
                }
                
                .user-message .message-content {
                    background-color: #007bff;
                    color: white;
                }
                
                .assistant-message .message-content {
                    background-color: #f8f9fa;
                    border: 1px solid #dee2e6;
                }
                
                .streaming-cursor {
                    animation: blink 1s infinite;
                }
                
                @keyframes blink {
                    0%, 50% { opacity: 1; }
                    51%, 100% { opacity: 0; }
                }
            `}</style>
        </div>
    );
};

export default AgentChat;
